import { useCallback, useEffect, useState } from 'react';
import { api, type AdminPublishFeeWaiver, type AdminPublishFeeWaiverKind } from '@/lib/api';

/** What each kind waives, and how many one grant may carry (the server's limits). */
const KINDS: Record<
  AdminPublishFeeWaiverKind,
  { label: string; max: number; noun: [string, string] }
> = {
  first_publish: { label: 'Free publish', max: 10, noun: ['free publish', 'free publishes'] },
  extra_listing: {
    label: 'Free Store listing',
    max: 50,
    noun: ['free Store listing', 'free Store listings'],
  },
};

/**
 * Free publishes and free Store listings — admin-granted waivers of a one-time
 * fee (ADR-033 and its amendment).
 *
 * A free publish covers the publish fee on ONE first publish by the user; a
 * free Store listing covers ONE extra listing slot ($50). The AI security
 * review and a listing's content check still bill the user. A waiver spent on
 * a submission that is rejected comes back on its own. Only an unused one can
 * be revoked here. Rendered on Payments → Customer Lookup beside the credit
 * grant, which is the other way an admin puts value on an account.
 */
export function FreePublishesCard({ userId }: { userId: string }) {
  const [available, setAvailable] = useState<Record<AdminPublishFeeWaiverKind, number> | null>(
    null,
  );
  const [kind, setKind] = useState<AdminPublishFeeWaiverKind>('first_publish');
  const [waivers, setWaivers] = useState<AdminPublishFeeWaiver[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [reason, setReason] = useState('');
  const [count, setCount] = useState('1');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantResult, setGrantResult] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.adminListPublishFeeWaivers(userId);
      setAvailable(result.availableByKind ?? { first_publish: result.available, extra_listing: 0 });
      setWaivers(result.waivers);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load free publishes');
    }
  }, [userId]);

  useEffect(() => {
    setAvailable(null);
    setWaivers([]);
    setGrantResult(null);
    void load();
  }, [load]);

  const parsedCount = Number.parseInt(count, 10);
  const countValid =
    Number.isInteger(parsedCount) && parsedCount >= 1 && parsedCount <= KINDS[kind].max;
  const canGrant = !grantLoading && reason.trim().length > 0 && countValid;

  const handleGrant = async () => {
    if (!canGrant) return;
    setGrantLoading(true);
    setGrantResult(null);
    try {
      const result = await api.adminGrantPublishFeeWaivers({
        user: userId,
        reason: reason.trim(),
        count: parsedCount,
        kind,
      });
      const [one, many] = KINDS[kind].noun;
      setGrantResult(
        `Granted ${result.waivers.length} ${result.waivers.length === 1 ? one : many}.`,
      );
      setReason('');
      setCount('1');
      await load();
    } catch (err) {
      setGrantResult(`Error: ${err instanceof Error ? err.message : 'Failed'}`);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevoke = async (waiverId: string) => {
    setRevokingId(waiverId);
    setGrantResult(null);
    try {
      await api.adminRevokePublishFeeWaiver(waiverId);
      await load();
    } catch (err) {
      setGrantResult(`Error: ${err instanceof Error ? err.message : 'Failed to revoke'}`);
    } finally {
      setRevokingId(null);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };

  const statusLabel = (w: AdminPublishFeeWaiver) => {
    if (w.status === 'consumed')
      return `used on ${w.consumedSubmissionId} · ${formatDate(w.consumedAt)}`;
    if (w.status === 'revoked') return `revoked ${formatDate(w.revokedAt)}`;
    return 'available';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-semibold text-foreground">Free Publishes &amp; Listings</h3>
        <p className="text-sm text-foreground" data-testid="free-publishes-available">
          {available === null
            ? '…'
            : `${available.first_publish} publish${available.first_publish === 1 ? '' : 'es'} · ${available.extra_listing} listing${available.extra_listing === 1 ? '' : 's'} available`}
        </p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        A free publish waives the one-time publishing fee on one first publish; a free Store listing
        waives one extra listing slot. The AI security review and a listing&apos;s content check are
        still billed. A waiver spent on a rejected submission comes back on its own.
      </p>

      {loadError && <p className="text-sm text-destructive mb-3">{loadError}</p>}

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-2 mb-3">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as AdminPublishFeeWaiverKind)}
          aria-label="Kind"
          className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
        >
          {(Object.keys(KINDS) as AdminPublishFeeWaiverKind[]).map((k) => (
            <option key={k} value={k}>
              {KINDS[k].label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required, e.g. Partner onboarding — Acme)"
          maxLength={500}
          aria-label="Reason"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
        />
        <input
          type="number"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          min="1"
          max={KINDS[kind].max}
          step="1"
          aria-label="Count"
          className="w-20 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
        />
        <button
          onClick={handleGrant}
          disabled={!canGrant}
          className="px-3 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {grantLoading ? 'Granting...' : `Grant ${KINDS[kind].noun[0]}`}
        </button>
      </div>
      {grantResult && (
        <p
          className={`text-sm mb-3 ${grantResult.startsWith('Error') ? 'text-destructive' : 'text-green-600'}`}
        >
          {grantResult}
        </p>
      )}

      {waivers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-muted-foreground font-medium">Granted</th>
                <th className="text-left py-2 text-muted-foreground font-medium">Kind</th>
                <th className="text-left py-2 text-muted-foreground font-medium">By</th>
                <th className="text-left py-2 text-muted-foreground font-medium">Reason</th>
                <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {waivers.map((w) => (
                <tr key={w.id} className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">{formatDate(w.createdAt)}</td>
                  <td className="py-2 text-muted-foreground">
                    {KINDS[w.kind ?? 'first_publish'].label}
                  </td>
                  <td className="py-2 text-foreground font-mono text-xs break-all">
                    {w.grantedByUserId}
                  </td>
                  <td className="py-2 text-foreground truncate max-w-xs">{w.reason}</td>
                  <td className="py-2 text-muted-foreground">{statusLabel(w)}</td>
                  <td className="py-2 text-right">
                    {w.status === 'available' && (
                      <button
                        onClick={() => handleRevoke(w.id)}
                        disabled={revokingId === w.id}
                        className="text-xs text-destructive hover:underline disabled:opacity-50"
                      >
                        {revokingId === w.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
